import { Activity, Clock, FileText } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { isOperatorShift } from '@/config/navigation';
import { useAuthStore } from '@/features/auth/auth.store';
import { useDocumentTitle } from '@/hooks/use-document-title';

/**
 * Puerta de las pantallas del turno.
 *
 * El turno es del **grifero** (grupo 2 de ion_auth): es quien está en la isla y quien
 * lo abre y lo cierra. El módulo `operadores.turnos` tiene ese grupo como permiso, así
 * que el administrador no lo ve en el catálogo; pero si escribe `/turno` en la barra
 * de direcciones llegaba al formulario de apertura y podía **abrir un turno a su
 * nombre**, que después nadie cierra (no está en la isla) y que deja al grifero sin
 * sus surtidores.
 *
 * Regla del negocio (2026-10-06): «un administrador no puede abrir turno». La API
 * rechaza las escrituras del turno con 403 (`TURNO_SOLO_OPERADOR`); aquí se explica lo
 * mismo en lugar de mostrar un formulario que fallaría al enviarse. La supervisión del
 * turno **no se pierde**: vive en *Turnos en vivo* y en *Reportes · Turnos*.
 *
 * @param titulo Título de la cabecera (`Mi turno`, `Cerrar turno`, …)
 * @param children Pantalla del turno (sólo se monta si la sesión es la del grifero)
 */
export function SoloOperadorTurno({ titulo, children }: { titulo: string; children: ReactNode }) {
  useDocumentTitle(`Turno · ${titulo}`);

  const groupIds = useAuthStore((state) => state.user?.groupIds);
  const navegar = useNavigate();

  if (isOperatorShift(groupIds)) return <>{children}</>;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Operador</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Clock className="size-6 text-brand-500" aria-hidden="true" />
          {titulo}
        </h1>
      </header>

      <SectionCard
        title="Esta pantalla es del operador de turno"
        description="El turno lo abre y lo opera el grifero asignado a la isla."
      >
        <div className="space-y-4">
          <Alert variant="warning" title="Tu cuenta no es de operador de turno">
            Un administrador no abre ni opera turnos: quedaría un turno abierto a su nombre que
            nadie cierra —no está en la isla— y dejaría al grifero sin sus surtidores. La API
            rechaza la apertura con un aviso de permisos.
          </Alert>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              leftIcon={<Activity className="size-4" aria-hidden="true" />}
              onClick={() => navegar('/turnos-en-vivo')}
            >
              Ver turnos en vivo
            </Button>
            <Button
              variant="outline"
              leftIcon={<FileText className="size-4" aria-hidden="true" />}
              onClick={() => navegar('/reportes/turnos')}
            >
              Reportes · Turnos
            </Button>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
